import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";

export type MarketingTab =
  | "calendar"
  | "campaigns"
  | "audiences"
  | "consent"
  | "results"
  | "contacts"
  | "landing-pages"
  | "triggers";

interface MarketingNavProps {
  active: MarketingTab;
}

interface NavItem {
  key: MarketingTab;
  label: string;
  to: string;
}

/**
 * Shared tab strip across the marketing workspace (Campaigns, Audiences,
 * Consent, Results). Rendered at the top of every marketing page so the
 * coordinator can move between surfaces at any drill level. Mirrors the
 * forecasting WorkspaceNav idiom.
 */
export function MarketingNav({ active }: MarketingNavProps) {
  // Ordered to mirror the campaign workflow: import contacts → check consent
  // → build audience → schedule → run campaign → landing pages → review
  // results. Results stays last by design — it's the "look back" tab, kept
  // clear of the day-to-day working tabs ahead of it.
  const items: NavItem[] = [
    { key: "contacts", label: "Contacts", to: paths.marketingPeople() },
    { key: "consent", label: "Consent", to: paths.marketingConsent() },
    { key: "audiences", label: "Audiences", to: paths.marketingAudiences() },
    { key: "calendar", label: "Calendar", to: paths.marketingCalendar() },
    { key: "campaigns", label: "Campaigns", to: paths.marketingCampaigns() },
    {
      key: "landing-pages",
      label: "Landing pages",
      to: paths.marketingLandingPages(),
    },
    { key: "triggers", label: "Triggers", to: paths.marketingTriggers() },
    { key: "results", label: "Results", to: paths.marketingResults() },
  ];

  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-border pb-2">
      {items.map((i) => {
        const isActive = i.key === active;
        return (
          <Link
            key={i.key}
            to={i.to}
            className={`px-3 py-1.5 text-sm rounded-md ${
              isActive
                ? "bg-primary/10 text-primary font-semibold"
                : "text-muted-foreground hover:text-foreground hover:bg-surface-hover"
            }`}
          >
            {i.label}
          </Link>
        );
      })}
    </div>
  );
}
